import { Component, OnInit } from '@angular/core';
import { BaseComponent } from '../../../base/base.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ProgressbarComponent } from '../../../core/labels/progressbar.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { EmptyStateComponent } from '../../../core/panels/empty-state.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
import { getClaimKey } from '../../../../functions/get-claim-key';
import {
  TenderDetailResponse,
  TenderItem,
} from '../../../../models/api/view/claim/details/tender/tender-detail-response';

@Component({
  selector: 'app-tender',
  imports: [
    PageHeaderComponent,
    CardComponent,
    DetailPanelComponent,
    DetailItemComponent,
    TableComponent,
    ColumnComponent,
    EmptyStateComponent,
    ProgressbarComponent,
  ],
  template: ` <app-page-header label="Tender" />
    <div class="tw-flex tw-gap-8 tw-pb-8">
      <div class="tw-basis-1/5">
        <app-detail-panel [dataSource]="tenderDetails">
          <app-detail-item
            label="Tender Opportunity"
            name="tenderOpportunity"
          />
          <app-detail-item label="Tendered" name="tendered" />
        </app-detail-panel>
      </div>
    </div>
    @if (loading) {
      <app-empty-state
        height="300px"
        icon="fa-light fa-file-contract"
        label="Loading tender details"
        message="Retrieving tender information..."
      >
        <div class="tw-py-4">
          <app-progressbar />
        </div>
      </app-empty-state>
    } @else if (tenderItems.length === 0) {
      <app-empty-state
        height="300px"
        icon="fa-light fa-file-contract"
        label="No tenders"
        message="There are no tender records available for this claim."
      />
    } @else {
      <app-table
        [dataSource]="tenderItems"
        [scrollable]="false"
        [expandable]="true"
        [expandOnFilter]="true"
        recordLabel="Tender"
        dataKey="id"
        [stripedRows]="false"
        [pageSize]="5"
        [pageSizeOptions]="[5, 10, 20]"
      >
        <app-column label="Bound" name="bound" width="20%" [sort]="true" />
        <app-column label="Name" name="name" width="20%" [sort]="true" />
        <app-column
          label="Type of tender"
          name="typeOfTender"
          width="20%"
          [sort]="true"
        />
        <app-column
          label="Notice date"
          name="noticeDate"
          width="20%"
          format="d"
          [sort]="true"
        />
        <app-column label="Status" name="status" width="20%" [sort]="true" />

        <ng-template #expand let-data>
          <div class="tw-flex tw-gap-8">
            <div class="tw-basis-1/4">
              <app-card label="Tender">
                <app-detail-panel [dataSource]="data" [showIcons]="false">
                  @if (isGlClaim) {
                    <app-detail-item label="Level" name="level" />
                  }
                  <app-detail-item label="Bound" name="bound" />
                  <app-detail-item label="Type of tender" name="typeOfTender" />
                  <app-detail-item label="Status" name="status" />
                  <app-detail-item
                    label="Post Tender Fees Owed"
                    name="postTenderFeesOwed"
                  />
                  <app-detail-item
                    label="Notice date"
                    name="noticeDate"
                    format="d"
                  />
                  <app-detail-item
                    label="Reply Date"
                    name="replyDate"
                    format="d"
                  />
                  <app-detail-item
                    label="Carrier Notified"
                    name="carrierNotified"
                    format="d"
                  />
                  <app-detail-item label="Comments" name="commentsSummary" />
                </app-detail-panel>
              </app-card>
            </div>
            <div class="tw-basis-3/4">
              <app-card label="Contact">
                <div class="tw-flex tw-gap-8">
                  <div class="tw-basis-1/2">
                    <app-detail-panel [dataSource]="data" [showIcons]="false">
                      <app-detail-item
                        label="Tender Party Type"
                        name="tenderPartyType"
                      />
                      <app-detail-item label="Name" name="name" />
                      <app-detail-item label="Contact" name="contact" />
                      <app-detail-item
                        label="Address 1"
                        name="address.address1"
                      />
                      <app-detail-item
                        label="Address 2"
                        name="address.address2"
                      />
                      <app-detail-item label="City" name="address.city" />
                      <app-detail-item label="State" name="address.state" />
                      <app-detail-item label="Zip" name="address.zip" />
                      <app-detail-item
                        label="Phone Type 1"
                        name="phone1.type"
                      />
                      <app-detail-item
                        label="Phone Number 1"
                        name="phone1.number"
                      />
                      <app-detail-item
                        label="Phone Type 2"
                        name="phone2.type"
                      />
                      <app-detail-item
                        label="Phone Number 2"
                        name="phone2.number"
                      />
                    </app-detail-panel>
                  </div>
                  <div class="tw-basis-1/2">
                    <app-detail-panel [dataSource]="data" [showIcons]="false">
                      <app-detail-item label="Email" name="email" />
                      <app-detail-item
                        label="External Claim Number"
                        name="externalClaimNumber"
                      />
                      @if (isOutbound(data)) {
                        <app-detail-item
                          label="Tender Policy Limit"
                          name="tenderPolicyLimit"
                          format="c"
                        />
                      }
                      <app-detail-item
                        label="Attorney Name"
                        name="attorney.name"
                      />
                      <app-detail-item
                        label="Attorney Phone"
                        name="attorney.phone"
                      />
                      <app-detail-item
                        label="Attorney Email"
                        name="attorney.email"
                      />
                      <app-detail-item
                        label="Insurance Company Name"
                        name="insuranceCompany.name"
                      />
                      <app-detail-item
                        label="Insurance Company Phone"
                        name="insuranceCompany.phone"
                      />
                      <app-detail-item
                        label="Insurance Company Email"
                        name="insuranceCompany.email"
                      />
                    </app-detail-panel>
                  </div>
                </div>
              </app-card>
            </div>
          </div>
        </ng-template>
      </app-table>
    }`,
  styles: [],
})
export class TenderComponent extends BaseComponent implements OnInit {
  tenderDetails: { tenderOpportunity: string; tendered: string } = {
    tenderOpportunity: '',
    tendered: '',
  };
  tenderItems: TenderItem[] = [];
  loading = true;

  lob: string;
  claim: any;
  isGlClaim = false;

  async ngOnInit(): Promise<void> {
    this.claim = this.view.currentClaim();
    this.lob = this.claim?.lineOfBusiness;
    this.setClaimLobValues(this.lob);

    const claimKey = getClaimKey();
    const response: TenderDetailResponse = await this.api.tender.get(claimKey);

    this.tenderDetails = {
      tenderOpportunity: response.tenderOpportunity,
      tendered: response.tendered,
    };

    // Filter out duplicate ids, keeping only the first occurrence
    const seenIds = new Set<number>();
    this.tenderItems = (response.tenderItems ?? []).filter((item) => {
      if (seenIds.has(item.id)) {
        return false;
      }
      seenIds.add(item.id);
      return true;
    });

    this.loading = false;
    this.view.hideLoading();
  }

  setClaimLobValues(lob: string): void {
    switch (lob) {
      case 'GL':
        this.isGlClaim = true;
        break;
      default:
        this.isGlClaim = false;
        break;
    }
  }

  isOutbound(item: TenderItem): boolean {
    return item?.bound === 'Outbound';
  }
}
